import { useCallback, useState } from 'react';
import { authManager } from '../services/authManager';

const TOKEN_KEY = 'soar_access_token';

export interface AuthState {
  token: string | null;
  accountId: string | null;
  onRefresh: (() => Promise<void | string>) | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

export interface AuthActions {
  login: (username?: string, password?: string) => Promise<string>;
  logout: () => void;
  setAuth: (auth: { accessToken?: string; accountId?: string; onRefresh?: () => Promise<void | string> }) => void;
}

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    token: localStorage.getItem(TOKEN_KEY),
    accountId: null,
    onRefresh: null,
    isLoading: false,
    error: null,
    isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  });

  const setAuth = useCallback((auth: { accessToken?: string; accountId?: string; onRefresh?: () => Promise<void | string> }) => {
    setState(prev => {
      const token = auth.accessToken || prev.token;
      const accountId = auth.accountId || prev.accountId;
      const onRefresh = auth.onRefresh || prev.onRefresh;

      authManager.setAuth({ token, accountId, onRefresh });

      if (prev.token === token && prev.accountId === accountId && prev.onRefresh === onRefresh) {
        return prev;
      }

      return {
        ...prev,
        token,
        accountId,
        onRefresh,
        isAuthenticated: !!token,
      };
    });
  }, []);

  const login = useCallback(async (username = 'user', password = 'password') => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const token = await authManager.login(username, password);

      setState(prev => ({
        ...prev,
        token,
        isLoading: false,
        isAuthenticated: true,
      }));
      return token;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        isLoading: false,
      }));
      console.error('Login error', err);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    authManager.setAuth({ token: null });
    setState(prev => ({
      ...prev,
      token: null,
      isAuthenticated: false,
    }));
  }, []);

  return {
    ...state,
    login,
    logout,
    setAuth,
  };
};
